import { Sparkles } from 'lucide-react';
import styled from 'styled-components';

import { ds } from '@/config/colors';
import { LocaleLink } from '@/utils/locale';

import { media } from '../../../utils/mediaQueries';

export const TopBarWrapper = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 100%;
    min-height: 0;
    position: sticky;
    top: 0;
    z-index: 10;
    transition: all 0.3s ease;

    ${media.tablet} {
        gap: 12px;
    }

    ${media.mobile} {
        gap: 8px;
    }
`;

export const LeftNav = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 0; /* Allow flex items to shrink below content size */
    overflow: hidden;

    ${media.tablet} {
        gap: 12px;
    }

    ${media.mobile} {
        gap: 8px;
    }
`;

export const RightNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0; /* Prevent right nav from shrinking */

    ${media.tablet} {
        gap: 8px;
    }

    ${media.mobile} {
        gap: 6px;
    }

    /* Ikony (pomoc, koperta, dzwonek) stylujemy w IconCircleButton —
       RightNav nie narzuca już własnych wymiarów potomkom, żeby nie
       nadpisywać okrągłego kształtu przycisków. */
`;

/* Slot UserMenu: trigger — awatar + chevron. Delikatny hover wokół całości. */
export const ProfileMenuSlot = styled.div`
    display: flex;
    align-items: center;

    /* && podbija specyficzność do poziomu dawnego selektora z RightNav (HeroUI ustawia padding triggera). */
    && button {
        display: flex;
        align-items: center;
        padding: 3px 4px 3px 3px;
        border-radius: 999px;
        transition: background-color 0.15s ease;

        &:hover {
            background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#f3f4f6')};
            opacity: 1;
        }
    }
`;

/* Hamburger otwierający menu na tablecie i telefonie: pole dotyku 40×40,
   ujemny margines wyrównuje samą ikonę do krawędzi paska. */
export const MobileNavigationHeadline = styled.button`
    width: 40px;
    height: 40px;
    margin-left: -10px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: transparent;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e7eb' : '#374151')};
    -webkit-tap-highlight-color: transparent;

    ${media.tabletDown} {
        display: inline-flex;
    }

    &:hover,
    &:active {
        background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : ds.bgMuted)};
    }

    &:focus-visible {
        outline: 2px solid ${ds.accentInk};
        outline-offset: 2px;
    }
`;

export const NotificationBadge = styled.span`
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    font-size: 10px;
    font-weight: 400;
    border-radius: 9999px;
    padding: 0 5px;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);

    ${media.mobile} {
        min-width: 16px;
        height: 16px;
        font-size: 9px;
        padding: 0 4px;
    }
`;

/**
 * Okrągły przycisk ikony w prawym TopBar (pomoc, koperta, dzwonek):
 * 38×38, białe tło i cienka szara ramka — wszystkie trzy tworzą spójny
 * rząd okręgów, wizualnie sparowany z awatarem obok.
 */
/* Przycisk ikonowy jak w hotelspot-audit: 36 px, rounded-full, bez ramki, hover bg-muted. */
export const IconCircleButton = styled.button`
    position: relative;
    width: 36px;
    height: 36px;
    padding: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: transparent;
    border: none;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.textSecondary)};
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
    appearance: none;
    -webkit-appearance: none;
    box-sizing: border-box;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    /* Bez overflow: hidden: NotificationBadge wystaje poza okrąg. */

    svg {
        width: 18px;
        height: 18px;
    }

    &:hover {
        background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : ds.bgMuted)};
        color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : ds.textPrimary)};
    }

    &:focus-visible {
        outline: 2px solid ${ds.accentInk};
        outline-offset: 2px;
    }
`;

/* Guzik „Zapytaj": bez ramki i tła, czarna ikona z iskrą i podkreślenie etykiety na hover.
   Gdy panel czatu jest otwarty ($active), podkreślenie zostaje na stałe. */
export const AskAiButton = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    box-sizing: border-box;
    cursor: pointer;
    flex-shrink: 0;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#111827')};
    font-size: 14px;
    font-weight: 500;

    span {
        text-decoration-line: ${({ $active }) => ($active ? 'underline' : 'none')};
        text-underline-offset: 4px;
        text-decoration-thickness: 1px;
    }

    &:hover span {
        text-decoration-line: underline;
    }

    &:active {
        transform: scale(0.97);
    }

    &:focus-visible {
        outline: 2px solid #111827;
        outline-offset: 2px;
    }

    ${media.tablet} {
        height: 34px;
    }

    /* Telefon: sama ikona w okręgu 36×36, jak sąsiednie przyciski. */
    ${media.mobile} {
        width: 36px;
        padding: 0;
        justify-content: center;
        border-radius: 9999px;
        background: ${({ $active, theme }) => {
    if (!$active) return 'transparent';
    return theme.mode === 'dark' ? '#242a33' : ds.bgMuted;
  }};
    }
`;

/* Ikona AI z iskrą (jak w audycie: Sparkles w kolorze teal), zamiast awatara asystenta. */
export const AskAiIcon = styled(Sparkles)`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    stroke-width: 1.75;
    color: currentColor;
`;

export const AskAiLabel = styled.span`
    white-space: nowrap;
    font-weight: 500;

    ${media.mobile} {
        display: none;
    }
`;

export const TestModeBadge = styled.div<{ $urgency?: 'normal' | 'warning' | 'critical' }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 6px;
    background: ${({ $urgency }) => {
    if ($urgency === 'critical') return 'rgba(239, 68, 68, 0.1)';
    if ($urgency === 'warning') return 'rgba(234, 148, 54, 0.1)';
    return 'rgba(107, 114, 128, 0.1)';
  }};
    border: 1px solid ${({ $urgency }) => {
    if ($urgency === 'critical') return 'rgba(239, 68, 68, 0.3)';
    if ($urgency === 'warning') return 'rgba(234, 148, 54, 0.3)';
    return 'rgba(107, 114, 128, 0.2)';
  }};
    white-space: nowrap;
    cursor: pointer;
    width: auto !important;
    height: auto !important;

    &:hover {
        background: ${({ $urgency }) => {
    if ($urgency === 'critical') return 'rgba(239, 68, 68, 0.15) !important';
    if ($urgency === 'warning') return 'rgba(234, 148, 54, 0.15) !important';
    return 'rgba(107, 114, 128, 0.15) !important';
  }};
        transform: none !important;
    }
`;

export const TestModeLabel = styled.span<{ $urgency?: 'normal' | 'warning' | 'critical' }>`
    font-size: 11px;
    font-weight: 500;
    color: ${({ $urgency }) => {
    if ($urgency === 'critical') return '#dc2626';
    if ($urgency === 'warning') return '#ea9436';
    return '#6b7280';
  }};
    line-height: 1.2;

    ${media.mobile} {
        font-size: 10px;
    }
`;

export const TestModeCountdown = styled.span<{ $urgency?: 'normal' | 'warning' | 'critical' }>`
    font-size: 11px;
    font-weight: 400;
    color: ${({ $urgency }) => {
    if ($urgency === 'critical') return '#b91c1c';
    if ($urgency === 'warning') return '#d97706';
    return '#374151';
  }};
    line-height: 1.2;

    ${media.mobile} {
        font-size: 10px;
    }
`;

/* Wrappery ikon w prawym TopBar — okrągłe 28×28 z szarym tłem i lekką ramką.
   Wzór 1:1 z `IconCircleButton` (Bell) i `UserMenu.AvatarWrapper` (avatar),
   żeby cała prawa nawigacja wyglądała spójnie. */
const iconCircleBase = `
    position: relative;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
    flex-shrink: 0;
`;

export const DisabledMobileIcon = styled.div`
    ${iconCircleBase}
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#f3f4f6')};
    border: 1.5px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#111827')};

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 18px;
        height: 1.5px;
        background: #6b7280;
        transform: translate(-50%, -50%) rotate(-45deg);
        pointer-events: none;
    }

    &:hover {
        background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
        border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#9ca3af')};
    }

    ${media.tablet} {
        width: 26px;
        height: 26px;
    }

    ${media.mobile} {
        width: 24px;
        height: 24px;
    }
`;

/* Logo jak w hotelspot-audit (BrandLogo): pełne 28 px, od desktopu 32 px; na telefonie sama sygnatura. */
export const BrandLink = styled(LocaleLink)`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    border-radius: 8px;

    .brand-full {
        display: block;
        height: 28px;
        width: auto;
    }

    .brand-sign {
        display: none;
        height: 28px;
        width: auto;
    }

    ${media.mobile} {
        .brand-full {
            display: none;
        }

        .brand-sign {
            display: block;
        }
    }

    &:focus-visible {
        outline: 2px solid ${ds.accentInk};
        outline-offset: 3px;
    }
`;

/* Kreska między logo a nazwą hotelu (jak między logo a „AI Visibility” w audycie). */
export const BrandDivider = styled.span`
    width: 1px;
    height: 24px;
    flex-shrink: 0;
    background: ${ds.borderDefault};

    ${media.mobile} {
        height: 20px;
    }
`;

/* Selektor organizacji w pasku, mniejszy (jak podpis „AI Visibility” w audycie): logo hotelu 24 px,
   nazwa 14 px, hover samo podkreślenie nazwy, bez tła i ramki. Style nadpisują wariant z sidebara. */
export const OrgSlot = styled.div`
    min-width: 0;
    max-width: 280px;

    & > div {
        width: auto;
        padding: 0;
    }

    button,
    button:hover:not(:disabled) {
        gap: 8px;
        padding: 4px 0;
        background: transparent;
        border-color: transparent;
        border-radius: 0;
    }

    /* logo hotelu (LogoBox ma rozmiar w stylu inline, stąd !important) */
    button > div:first-child > div,
    & > div > div > div:first-child > div {
        width: 24px !important;
        height: 24px !important;
        border-radius: 6px;
        box-shadow: none;

        img {
            padding: 2px;
        }
    }

    span {
        font-size: 14px;
        font-weight: 500;
        color: ${ds.textPrimary};
        text-underline-offset: 4px;
    }

    button:hover:not(:disabled) span {
        text-decoration: underline;
    }

    svg {
        width: 14px;
        height: 14px;
        color: ${ds.textMuted};
    }
`;
